import { Amplify, Auth, Hub } from 'aws-amplify';
import cryptoRandomString from 'crypto-random-string';
import { usePostHog } from 'posthog-js/react';
import { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Api } from '../services/base.api';
import { CognitoDeploymentResponse } from '../services/deployment.api';
import { useLogger } from '../services/logging/logger-context';
import { i18n } from '../services/translation/i18n';
import { ROUTES } from '../types/routes.enum';
import { getDomainFromEmail, validateEmail } from './utils';
import { EmailOrPhoneValidationError } from '../errors/email-or-phone-validation-error';
import { CodeError } from '../constant/text.constant';
import { UserAlreadyExistsError } from '../errors/user-already-exists-error';
import { UserAuthenticationError } from '../errors/user-authentication-error';
import { UserNotFoundError } from '../errors/user-not-found-error';
import { normalizeLocale } from '../util/locale-utility';

export enum USER_ROLE {
  SUPPORT = 'SUPPORT',
  NORMAL = 'NORMAL',
  DRIVER = 'DRIVER',
}

export enum AuthFlow {
  OTP,
  SSO,
}

export type SignInResult = { flow: AuthFlow.OTP; user: User } | { flow: AuthFlow.SSO };

export interface UserAttributes {
  address?: {
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    country?: string;
    isoCode?: string;
    postalCode?: string;
    province?: string;
  };
  'custom:legacy_support_da'?: string;
  'custom:stable_user_id'?: string;
  email?: string;
  email_verified?: boolean;
  family_name?: string;
  given_name?: string;
  phone_number?: string;
  phone_number_verified?: boolean;
  profile?: {
    sitehostDashboardLanguage?: string;
    acceptedDriverTerms?: string;
  };
  sub?: string;
}

export interface User {
  id?: string;
  username?: string;
  attributes: UserAttributes;
}

type ContextType = {
  signIn: Function;
  signOut: (redirectRoute?: string) => void;
  signUp: Function;
  resendCode: Function;
  verifyCode: Function;
  setToken: Function;
  cleanError: Function;
  setCognitoUser: Function;
  handleUserChange: Function;
  cognitoUser: any;
  user: any;
  role: string;
  token: string;
  error: string;
  isLoggedIn: boolean;
  refreshUserData: Function;
};

const initalState: ContextType = {
  signIn: () => {},
  signOut: () => {},
  signUp: () => {},
  resendCode: () => {},
  verifyCode: () => {},
  setToken: () => {},
  cleanError: () => {},
  setCognitoUser: () => {},
  handleUserChange: () => {},
  cognitoUser: {},
  user: {
    attributes: {
      sub: '',
      email_verified: true,
      phone_number_verified: true,
      email: '',
      family_name: '',
      given_name: '',
      phone_number: '',
      profile: {
        sitehostDashboardLanguage: 'en-US',
        acceptedDriverTerms: '1.0',
      },
      address: {
        addressLine1: '',
        addressLine2: '',
        city: '',
        province: '',
        country: '',
        postalCode: '',
        isoCode: '',
      },
    },
    username: '',
  },
  role: USER_ROLE.NORMAL,
  token: '',
  error: '',
  isLoggedIn: false,
  refreshUserData: () => {},
};

const transformUserInfo = (newUserinfo: any) => {
  let profile;
  let address;
  if (newUserinfo.attributes?.profile) {
    profile = JSON.parse(newUserinfo.attributes.profile);
  }
  newUserinfo.attributes.profile = {
    ...profile, // Spread all existing fields first (preserves fields from other applications)
    acceptedDriverTerms: profile?.acceptedDriverTerms,
  };
  if (newUserinfo.attributes?.address) {
    address = JSON.parse(newUserinfo.attributes.address);
  }
  newUserinfo.attributes.address = {
    addressLine1: address?.addressLine1 || '',
    addressLine2: address?.addressLine2 || '',
    city: address?.city || '',
    province: address?.province || '',
    country: address?.country || '',
    postalCode: address?.postalCode || '',
    isoCode: address?.isoCode || '',
  };

  return newUserinfo;
};

interface AuthProviderOauthConfig {
  domain: string;
  clientId: string;
  redirectSignIn: string;
  redirectSignOut: string;
  ssoDomainProviderMap: Record<string, string>;
  ssoOtpDomainAllowlist: string[];
}

const useProvideAuth = (oauthConfig: AuthProviderOauthConfig) => {
  const { logger } = useLogger();
  const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState('');
  const [token, setToken] = useState('');
  const [error, setError] = useState('');
  const [cognitoUser, setCognitoUser] = useState<any>(null);
  const posthog = usePostHog();

  const dispatch = useDispatch();

  const handleUserChange = (newUser: User) => {
    if (!newUser) {
      return;
    }
    if (newUser.attributes['custom:stable_user_id']) {
      posthog.identify(newUser.attributes['custom:stable_user_id']);
      // Add some posthog user properties for identify
    }
    posthog.setPersonProperties({ email_domain: getDomainFromEmail(newUser.attributes?.email) });

    setUser(newUser);
  };
  const isLoggedIn = useMemo(() => {
    return user != null;
  }, [user]);

  const signIn = async (loginString: string): Promise<SignInResult> => {
    if (!loginString) {
      setError('Please enter your email or phone number.');
      throw new EmailOrPhoneValidationError();
    }
    const isEmail = validateEmail(loginString);

    // If login string is an email and the domain requires SSO sign-in, sign in with SSO
    if (isEmail) {
      const emailDomain = getDomainFromEmail(loginString);
      if (emailDomainRequiresSso(emailDomain)) {
        return signInWithSso(emailDomain);
      }
    }

    // If login string is not an email, always use OTP sign-in
    // The Lambda functions will return an error if a phone number is entered
    // and the email address associated with that user's account requires an SSO login
    // this will cause the sign-in to fail
    return signInWithOtp(loginString, isEmail);
  };

  const signInWithOtp = async (loginString: string, isEmail: boolean): Promise<SignInResult> => {
    try {
      const user = await Auth.signIn(loginString);
      await Auth.sendCustomChallengeAnswer(user, loginString, {
        isEmail: `${isEmail}`,
      });
      setCognitoUser(user);
      return { flow: AuthFlow.OTP, user: user };
    } catch (e: any) {
      if (e.code === CodeError.COGNITO_LAMBDA_USER_NOT_FOUND) {
        throw new UserNotFoundError();
      } else if (e.code === CodeError.COGNITO_LAMBDA_ERROR_CODE) {
        logger.error('UserAuthenticationError in signInWithOtp:', e.message, e.stack);
        throw new UserAuthenticationError();
      } else {
        logger.error('Unexpected error in signInWithOtp:', e.message, e.stack);
        throw e;
      }
    }
  };

  const signInWithSso = async (domain: string): Promise<SignInResult> => {
    // IMPORTANT: Store the current location so we can return here after SSO completes.
    // The OAuth state parameter is the only way to pass data through the SSO round-trip.
    //
    // Flow:
    // 1. User is at some path (e.g., /app/chargelab/login?charger=CoolCharger1)
    // 2. We encode that path as base64 JSON in the OAuth state parameter
    // 3. User is redirected to external SSO provider
    // 4. After authentication, SSO provider redirects back with our state parameter
    // 5. Amplify fires 'customOAuthState' Hub event with our decoded state
    // 6. We redirect user back to their original path
    const returnPath = window.location.pathname + window.location.search;
    const customState = btoa(JSON.stringify({ returnPath }));

    // Redirect to SSO provider with custom state
    const provider = oauthConfig.ssoDomainProviderMap[domain];
    await Auth.federatedSignIn({
      customProvider: provider,
      customState: customState,
    });
    return { flow: AuthFlow.SSO };
  };

  const isUserFederated = useMemo(() => {
    if (!user || !user.attributes) return false;
    try {
      const identities = user.attributes['identities'];
      if (identities) {
        const parsedIdentities =
          typeof identities === 'string' ? JSON.parse(identities) : identities;
        return (
          Array.isArray(parsedIdentities) &&
          parsedIdentities.some((identity: any) => identity.providerType === 'SAML')
        );
      }
    } catch (e) {
      logger.error('Error parsing user identities:', e);
    }
    return false;
  }, [user, logger]);

  const emailDomainIsSsoDomain = (emailDomain: string): boolean => {
    // If domain is not in the SSO domain provider map, return false
    if (!oauthConfig.ssoDomainProviderMap[emailDomain]) {
      return false;
    }
    return true;
  };

  const emailDomainRequiresSso = (emailDomain: string): boolean => {
    // If domain is not an SSO domain, return false
    if (!emailDomainIsSsoDomain(emailDomain)) {
      return false;
    }
    // If domain is in OTP allowlist (transition period), return false
    if (oauthConfig.ssoOtpDomainAllowlist.includes(emailDomain)) {
      return false;
    }
    // Otherwise, return true
    return true;
  };

  const signUp = async (
    firstName: string,
    lastName: string,
    email: string,
    phoneNumber: string,
  ) => {
    try {
      const trimmedEmail = email.trim();
      const isValidEmail = validateEmail(trimmedEmail);
      if (!isValidEmail) {
        throw new EmailOrPhoneValidationError();
      }
      const emailDomain = getDomainFromEmail(trimmedEmail);
      // If email domain requires SSO, sign up with SSO
      if (emailDomainRequiresSso(emailDomain)) {
        signInWithSso(emailDomain);
      } else {
        await Auth.signUp({
          username: cryptoRandomString({ length: 16, type: 'alphanumeric' }),
          password: cryptoRandomString({
            length: 120,
            type: 'ascii-printable',
          }), // May have a chance to not comply with the Cognito password rules
          attributes: {
            given_name: firstName,
            family_name: lastName,
            email: trimmedEmail,
            phone_number: phoneNumber,
            profile: JSON.stringify({
              acceptedDriverTerms: '1.0',
              sitehostDashboardLanguage: i18n.language || 'en-US',
            }),
          },
        });
      }
    } catch (e: any) {
      setError(e.message);
      if (e.code === CodeError.COGNITO_LAMBDA_ERROR_CODE) {
        if (
          e.message.includes('email address already exists') ||
          e.message.includes('phone number already exists')
        ) {
          throw new UserAlreadyExistsError();
        } else {
          logger.error('UserAuthenticationError in signUp:', e.message, e.stack);
          throw new UserAuthenticationError();
        }
      } else {
        logger.error('Unexpected error in signUp:', e.message, e.stack);
        throw e;
      }
    }
  };

  const refreshUserData = async () => {
    const userInfo = await Auth.currentUserInfo();
    handleUserChange(transformUserInfo(userInfo));
  };

  const resendCode = () => {
    Auth.signIn(cognitoUser.username)
      .then((data) => {
        Auth.sendCustomChallengeAnswer(data, cognitoUser.username, {
          isEmail: `${validateEmail(cognitoUser.username)}`,
        }).then((newCognitoUser) => {
          setCognitoUser(newCognitoUser);
        });
      })
      .catch((e: any) => setError(e.message));
  };

  const verifyCode = (code: string) => {
    Auth.sendCustomChallengeAnswer(cognitoUser, code).catch((e) => {
      setError(`verifyCode failed: ${e.message}, ${e}`);
    });
  };

  const cleanup = () => {
    dispatch(Api.util.resetApiState());
    localStorage.clear();
    setCognitoUser(null);
    setUser(null);
    setToken('');
    posthog.reset();
  };

  const signOut = (redirectRoute?: string) => {
    try {
      const route = redirectRoute || `/${ROUTES.LOGIN}`;

      // For federated users, skip Auth.signOut and manually redirect to Cognito logout endpoint
      if (isUserFederated) {
        // Store the route to redirect to after logout
        sessionStorage.setItem('postLogoutRoute', route);
        // Redirect to Cognito logout endpoint
        const logoutUrl = `https://${oauthConfig.domain}/logout?client_id=${
          oauthConfig.clientId
        }&logout_uri=${encodeURIComponent(oauthConfig.redirectSignOut)}`;
        window.location.replace(logoutUrl);
        cleanup();
      } else {
        // For non-federated users, use standard Auth.signOut
        Auth.signOut().finally(() => {
          window.location.replace(route);
          cleanup();
        });
      }
    } catch (e: any) {
      setError(`error signing out: ${e.message}`);
      logger.error('Unexpected signOut error:', e.message, e.stack);
    }
  };

  useEffect(() => {
    if (user) {
      if (user?.attributes['custom:legacy_support_da'] === 'ALLOW') {
        setRole(USER_ROLE.SUPPORT);
      } else {
        setRole(USER_ROLE.NORMAL);
      }
    }
  }, [user]);

  useEffect(() => {
    const unsubscribe = Hub.listen('auth', (data) => {
      switch (data.payload.event) {
        case 'signIn':
          setCognitoUser(data.payload.data);
          Auth.currentUserInfo()
            .then((signInUserInfo) => {
              const transFormedRes = transformUserInfo(signInUserInfo);
              handleUserChange(transFormedRes);
            })
            .catch((e) => {
              logger.error('Error getting user info:', e);
            });
          break;
        case 'signUp':
          break;
        case 'signOut':
          break;
        case 'signIn_failure':
          logger.error('signIn_failure event:', new Error(JSON.stringify(data.payload)));
          break;
        case 'parsingCallbackUrl':
          break;
        case 'tokenRefresh':
          break;
        case 'tokenRefresh_failure':
          break;
        case 'configured':
          break;
        case 'customOAuthState':
          // This event fires when Amplify processes an OAuth callback with custom state.
          // The custom state contains the returnPath we stored before initiating SSO sign-in.
          // This is the PRIMARY and RELIABLE way to handle OAuth redirects after successful sign-in.

          try {
            // Decode the base64-encoded state that we created in signInWithSso()
            const stateData = JSON.parse(atob(data.payload.data));
            const returnPath = stateData.returnPath || '/';

            // Use window.location.replace() because:
            // 1. Doesn't create a new browser history entry
            // 2. Full page reload ensures clean initialization after OAuth
            // 3. Ensures all auth state is properly established before app logic runs
            window.location.replace(returnPath);
          } catch (e) {
            // Fallback to account page if state parsing fails
            if (window.domainName) {
              window.location.replace(`/app/${window.domainName}/${ROUTES.ACCOUNT}`);
            }
          }
          break;
        default:
          break;
      }
    });
    // Cleanup subscription on unmount
    return () => {
      unsubscribe();
    };
  }, []);

  const cleanError = () => setError('');
  // Return the user object and auth methods
  return {
    user,
    cognitoUser,
    role,
    token,
    error,
    signIn,
    signUp,
    signOut,
    setToken,
    resendCode,
    verifyCode,
    cleanError,
    setCognitoUser,
    handleUserChange,
    isLoggedIn,
    refreshUserData,
  };
};

const authContext = createContext(initalState);

export const useAuth = () => {
  return useContext(authContext);
};

interface AuthProviderProps {
  children: React.ReactNode;
  cognitoData: CognitoDeploymentResponse;
}

export const AuthProvider = ({ children, cognitoData }: AuthProviderProps) => {
  const { logger } = useLogger();
  const [authReady, setAuthReady] = useState<boolean>(false);

  // Build OAuth configuration from deployment data
  // Use a single canonical redirect URI - the OAuth state parameter will store the return path
  const oauthConfig = useMemo<AuthProviderOauthConfig>(
    () => ({
      domain: cognitoData?.oauth?.domain ?? '',
      clientId: cognitoData.clientId,
      redirectSignIn: `${window.location.origin}/auth/${ROUTES.AUTH_CALLBACK}`,
      redirectSignOut: `${window.location.origin}/auth/${ROUTES.AUTH_SIGNOUT}`,
      ssoDomainProviderMap: cognitoData?.oauth?.sso_domain_provider_map ?? {},
      ssoOtpDomainAllowlist: cognitoData?.oauth?.sso_otp_domain_allowlist ?? [],
    }),
    [cognitoData],
  );

  const auth = useProvideAuth(oauthConfig);

  useEffect(() => {
    const setupCognito = async () => {
      const authConfig: any = {
        region: cognitoData.region,
        userPoolId: cognitoData.userPoolId,
        userPoolWebClientId: cognitoData.clientId,
        mandatorySignIn: false,
        authenticationFlowType: 'CUSTOM_AUTH',
        oauth: {
          domain: oauthConfig.domain,
          redirectSignIn: oauthConfig.redirectSignIn,
          redirectSignOut: oauthConfig.redirectSignOut,
          scope: ['email', 'profile', 'openid', 'aws.cognito.signin.user.admin'],
          responseType: 'code',
        },
      };

      Amplify.configure({
        Auth: authConfig,
      });

      try {
        const cognitoUser = await Auth.currentAuthenticatedUser();
        auth.setCognitoUser(cognitoUser);

        const userInfo = await Auth.currentUserInfo();
        auth.handleUserChange(transformUserInfo(userInfo));

        const cognitoUserSession = await Auth.currentSession();
        const token = cognitoUserSession.getAccessToken().getJwtToken();
        auth.setToken(token);
      } catch (e: any) {
        if (typeof e === 'string') {
          // This throws when amplify is configured and no user is signed in.
          // This is expected behaviour, so no need to log it.
          if (e !== 'The user is not authenticated') {
            logger.error('setupCognito error: ', new Error(e));
          }
        } else {
          logger.error('Non-string error thrown in setupCognito:', e);
        }
      }
      setAuthReady(true);
    };

    setupCognito();
  }, [logger, oauthConfig, cognitoData]);

  if (!authReady) {
    return null;
  }
  return <authContext.Provider value={auth}>{children}</authContext.Provider>;
};
